import { useState, useMemo } from 'react';
import Swal from 'sweetalert2';
import useCategorias from '../../hooks/useCategorias';
import { recortarLogoCentrado } from '../../utils/recortarLogoInstitucional';
import { ICONOS_PRESET_CATEGORIA, CANTIDAD_ICONOS_FRECUENTES, ICONO_CATEGORIA_POR_DEFECTO } from '../../utils/iconosCategoria';
import DataTable from '../../components/Panel/DataTable';
import BotonAccionIcono from '../../components/Panel/BotonAccionIcono';
import ModalSelectorIcono from '../../components/Panel/ModalSelectorIcono';
import usePaginacionLocal from '../../hooks/usePaginacionLocal';
import useFiltroBusqueda from '../../hooks/useFiltroBusqueda';
import PanelShell from './PanelShell';
import '../../styles/Panel.css';

const CATEGORIA_VACIA = { cat_chNombre: '', cat_chCodigo: '', cat_chdescripcion: '', cat_fileLogo: null, cat_chIconoPreset: null, cat_inOrden: 0 };

/**
 * Módulo de Categorías (HU-009-D) — antes vivía escondida como una tarjeta
 * más dentro de Configuración; se separa a su propia pantalla del grupo
 * "Operaciones" del menú, con la misma tabla (`DataTable`) que usan
 * Usuarios/Roles/Vías en vez de la fila de chips que tenía antes. La lógica
 * de datos (`useCategorias`) y el recorte/selector de ícono
 * (`recortarLogoCentrado`/`ICONOS_PRESET_CATEGORIA`, HU-009-C) no cambian,
 * solo se movieron de archivo.
 */
const GestionCategorias = () => {
  const { categorias, loading, error, crearCategoria, actualizarCategoria, eliminarCategoria, cargarCategorias } = useCategorias();
  const { busqueda, setBusqueda, itemsFiltrados } = useFiltroBusqueda(
    categorias,
    (c) => `${c.cat_chNombre} ${c.cat_chCodigo} ${c.cat_chdescripcion}`
  );
  const [orden, setOrden] = useState('prioridad'); // 'prioridad' | 'alfabetico'

  // Ordena la lista filtrada según el toggle (HU-023).
  const itemsOrdenados = useMemo(() => {
    const lista = [...itemsFiltrados];
    if (orden === 'alfabetico') {
      lista.sort((a, b) => (a.cat_chNombre || '').localeCompare(b.cat_chNombre || '', 'es'));
    } else {
      lista.sort((a, b) => {
        const oa = Number(a.cat_inOrden) || 0;
        const ob = Number(b.cat_inOrden) || 0;
        return oa !== ob ? oa - ob : (a.cat_chNombre || '').localeCompare(b.cat_chNombre || '', 'es');
      });
    }
    return lista;
  }, [itemsFiltrados, orden]);
  const { datosPagina, paginacion } = usePaginacionLocal(itemsOrdenados);

  const [formulario, setFormulario] = useState(null); // null | CATEGORIA_VACIA | categoría existente
  const [logoPreview, setLogoPreview] = useState(null);
  const [mensaje, setMensaje] = useState('');
  const [selectorIconoAbierto, setSelectorIconoAbierto] = useState(false);

  const esEdicion = formulario && formulario.cat_inId;

  const guardarFormulario = async (evento) => {
    evento.preventDefault();
    setMensaje('');
    // Si no subió logo propio ni eligió ícono de la librería, no se guarda
    // en blanco — se asigna el genérico por defecto (antes solo se veía en
    // la tabla al mostrarla, pero el valor real quedaba vacío).
    const datos = (!formulario.cat_fileLogo && !formulario.cat_chIconoPreset)
      ? { ...formulario, cat_chIconoPreset: ICONO_CATEGORIA_POR_DEFECTO }
      : formulario;
    const resultado = esEdicion
      ? await actualizarCategoria(datos.cat_inId, datos)
      : await crearCategoria(datos);

    if (resultado.success) {
      setFormulario(null);
      setLogoPreview(null);
      cargarCategorias();
    } else {
      setMensaje(resultado.error);
    }
  };

  const handleLogo = async (evento) => {
    const archivo = evento.target.files?.[0];
    evento.target.value = '';
    if (!archivo) return;
    setMensaje('');
    try {
      const iconoRecortado = await recortarLogoCentrado(archivo);
      setFormulario({ ...formulario, cat_fileLogo: iconoRecortado, cat_chIconoPreset: null });
      setLogoPreview(URL.createObjectURL(iconoRecortado));
    } catch (err) {
      setMensaje(err.message);
    }
  };

  const elegirIconoPreset = (clase) => {
    setLogoPreview(null);
    setFormulario({ ...formulario, cat_fileLogo: null, cat_chIconoPreset: clase });
  };

  const handleEliminar = async (categoria) => {
    const confirmacion = await Swal.fire({
      icon: 'warning',
      title: '¿Eliminar categoría?',
      text: `"${categoria.cat_chNombre}" — esta acción no se puede deshacer.`,
      showCancelButton: true,
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#e63946',
    });
    if (!confirmacion.isConfirmed) return;
    const resultado = await eliminarCategoria(categoria.cat_inId);
    if (resultado.success) cargarCategorias();
    else setMensaje(resultado.error);
  };

  const columnas = useMemo(() => [
    {
      Header: 'Ícono',
      id: 'icono',
      Cell: ({ row }) => {
        const cat = row.original;
        if (cat.cat_fileLogo) return <img src={cat.cat_fileLogo} alt="" className="panel-config-logo-preview" style={{ width: 32, height: 32 }} />;
        return <i className={`fas ${cat.cat_chIconoPreset || 'fa-triangle-exclamation'} panel-config-logo-preview-icono`} style={{ width: 32, height: 32, fontSize: 14 }}></i>;
      },
    },
    { Header: 'Nombre', accessor: 'cat_chNombre' },
    { Header: 'Código', accessor: 'cat_chCodigo' },
    { Header: 'Prioridad', accessor: 'cat_inOrden' },
    { Header: 'Descripción', accessor: 'cat_chdescripcion' },
    {
      Header: 'Acciones',
      id: 'acciones',
      Cell: ({ row }) => (
        <div className="panel-gu-fila-acciones">
          <BotonAccionIcono icono="fa-pen" variante="editar" titulo="Editar" onClick={() => setFormulario({ ...row.original, cat_fileLogo: null })} />
          <BotonAccionIcono icono="fa-trash" variante="eliminar" titulo="Eliminar" onClick={() => handleEliminar(row.original)} />
        </div>
      ),
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], []);

  return (
    <PanelShell>
      <div className="panel-gu-cabecera">
        <h2>Categorías</h2>
        <div className="panel-gu-acciones">
          <button type="button" className="panel-boton-header-accion" onClick={() => setFormulario({ ...CATEGORIA_VACIA })}>
            <i className="fas fa-plus"></i> Nueva categoría
          </button>
        </div>
      </div>

      <div className="panel-gu-filtros">
        <input
          className="panel-input"
          placeholder="Buscar por nombre, código o descripción…"
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
        />
        <select
          className="panel-input"
          style={{ maxWidth: 240 }}
          value={orden}
          onChange={(e) => setOrden(e.target.value)}
        >
          <option value="prioridad">Ordenar por prioridad</option>
          <option value="alfabetico">Ordenar alfabéticamente</option>
        </select>
      </div>

      {mensaje && <p className="panel-gu-mensaje">{mensaje}</p>}
      {error && <p className="panel-gu-mensaje">{error}</p>}

      <DataTable
        columns={columnas}
        data={datosPagina}
        filaVacia={loading ? 'Cargando…' : 'No hay categorías todavía.'}
        paginacion={paginacion}
      />

      {formulario && (
        <div className="panel-gu-modal-fondo" onClick={() => setFormulario(null)}>
          <form className="panel-gu-modal" onClick={(e) => e.stopPropagation()} onSubmit={guardarFormulario}>
            <h3>{esEdicion ? 'Editar categoría' : 'Nueva categoría'}</h3>

            <label className="panel-label">Nombre</label>
            <input className="panel-input" required value={formulario.cat_chNombre}
              onChange={(e) => setFormulario({ ...formulario, cat_chNombre: e.target.value })} />

            <label className="panel-label">Código (opcional)</label>
            <input className="panel-input" value={formulario.cat_chCodigo || ''}
              onChange={(e) => setFormulario({ ...formulario, cat_chCodigo: e.target.value })} />

            <label className="panel-label">Descripción (opcional)</label>
            <input className="panel-input" value={formulario.cat_chdescripcion || ''}
              onChange={(e) => setFormulario({ ...formulario, cat_chdescripcion: e.target.value })} />

            <label className="panel-label">Prioridad de visualización (opcional)</label>
            <input className="panel-input" type="number" value={formulario.cat_inOrden ?? 0}
              onChange={(e) => setFormulario({ ...formulario, cat_inOrden: parseInt(e.target.value, 10) || 0 })} />
            <p className="panel-texto-ayuda">Número menor = aparece primero. Si todas quedan en 0, se ordenan alfabéticamente.</p>

            <label className="panel-label">Ícono</label>
            <div className="panel-config-logo-fila">
              {(() => {
                const src = logoPreview || (typeof formulario.cat_fileLogo === 'string' ? formulario.cat_fileLogo : null);
                if (src) return <img className="panel-config-logo-preview" src={src} alt="Ícono" />;
                if (formulario.cat_chIconoPreset) {
                  return <i className={`fas ${formulario.cat_chIconoPreset} panel-config-logo-preview-icono`}></i>;
                }
                return null;
              })()}
              <label className="panel-boton-secundario panel-gu-label-archivo">
                {esEdicion ? 'Cambiar ícono' : 'Subir ícono propio'}
                <input type="file" accept="image/png" onChange={handleLogo} hidden />
              </label>
            </div>
            <p className="panel-texto-ayuda">O elige uno de la librería (solo PNG si subes el tuyo):</p>
            <div className="panel-config-iconos-grid">
              {ICONOS_PRESET_CATEGORIA.slice(0, CANTIDAD_ICONOS_FRECUENTES).map((icono) => (
                <button
                  key={icono.clase}
                  type="button"
                  title={icono.etiqueta}
                  className={`panel-config-icono-opcion ${formulario.cat_chIconoPreset === icono.clase ? 'panel-config-icono-opcion-activo' : ''}`}
                  onClick={() => elegirIconoPreset(icono.clase)}
                >
                  <i className={`fas ${icono.clase}`}></i>
                </button>
              ))}
            </div>
            <button type="button" className="panel-boton-secundario panel-selector-icono-ver-mas" onClick={() => setSelectorIconoAbierto(true)}>
              <i className="fas fa-ellipsis"></i> Ver más íconos ({ICONOS_PRESET_CATEGORIA.length})
            </button>

            <div className="panel-gu-modal-botones">
              <button type="button" className="panel-boton-secundario" onClick={() => setFormulario(null)}>Cancelar</button>
              <button type="submit" className="panel-boton-primario" disabled={loading}>
                {loading ? 'Guardando…' : 'Guardar'}
              </button>
            </div>
          </form>
        </div>
      )}

      {selectorIconoAbierto && (
        <ModalSelectorIcono
          onSeleccionar={(clase) => { elegirIconoPreset(clase); setSelectorIconoAbierto(false); }}
          onCerrar={() => setSelectorIconoAbierto(false)}
        />
      )}
    </PanelShell>
  );
};

export default GestionCategorias;
